import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import { Helmet } from "react-helmet-async";
import { STATUS_OPTIONS } from "@/components/admin/adminFields";
import NotesEditor from "@/components/pilgrim/NotesEditor";
import DocumentsSection from "@/components/pilgrim/DocumentsSection";
import PaymentProofSection from "@/components/pilgrim/PaymentProofSection";
import DashboardSummary from "@/components/pilgrim/DashboardSummary";
import ReferralCard from "@/components/pilgrim/ReferralCard";
import ProfilePanel from "@/components/pilgrim/ProfilePanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarDays, Package as PackageIcon, Users, MessageCircle, LogOut, CheckCircle2, Loader2, LayoutDashboard, User as UserIcon, ClipboardList } from "lucide-react";

const MANASIK_STEPS = [
  { d: -60, title: "Manasik Umum #1", desc: "Pemahaman rukun & wajib umrah, sesi tanya jawab." },
  { d: -45, title: "Manasik Umum #2", desc: "Praktik ihram, thawaf, sa'i, tahallul dengan simulasi." },
  { d: -30, title: "Manasik Khusus Rombongan", desc: "Briefing tim, pembagian kamar, mutawwif rombongan." },
  { d: -14, title: "Pelunasan & Perlengkapan", desc: "Pelunasan pembayaran, pengambilan koper & perlengkapan umrah." },
  { d: -7, title: "Pengarahan Akhir", desc: "Briefing keberangkatan, cek kesehatan, vaksin." },
  { d: 0, title: "Keberangkatan", desc: "Kumpul di bandara, check-in, doa keberangkatan." },
];

const StatusBadge = ({ status }) => {
  const s = STATUS_OPTIONS.find((x) => x.v === (status || "new")) || STATUS_OPTIONS[0];
  return <span className={`text-[10px] font-medium uppercase tracking-widest rounded-full px-2.5 py-1 ${s.color}`}>{s.label}</span>;
};

const PayBadge = ({ ps }) => {
  const map = {
    unpaid: { l: "Belum DP", c: "bg-red-100 text-red-800" },
    dp_paid: { l: "DP Lunas", c: "bg-amber-100 text-amber-900" },
    paid_full: { l: "Lunas", c: "bg-emerald-100 text-emerald-800" },
  };
  const s = map[ps] || map.unpaid;
  return <span className={`text-[10px] font-medium uppercase tracking-widest rounded-full px-2.5 py-1 ${s.c}`}>{s.l}</span>;
};

function ManasikTimeline({ departureDate }) {
  if (!departureDate) return null;
  const dep = new Date(departureDate);
  const now = new Date();
  return (
    <div className="mt-6">
      <p className="overline mb-4">Jadwal Manasik & Keberangkatan</p>
      <ol className="relative border-l-2 border-accent/40 ml-3 space-y-6">
        {MANASIK_STEPS.map((step) => {
          const stepDate = new Date(dep.getTime() + step.d * 86400000);
          const done = stepDate < now;
          const isDeparture = step.d === 0;
          return (
            <li key={step.title} className="ml-6 relative">
              <span className={`absolute -left-[34px] top-0 h-4 w-4 rounded-full border-2 ${done ? "bg-accent border-accent" : isDeparture ? "bg-primary border-accent" : "bg-white border-accent/50"}`}>
                {done && <CheckCircle2 className="h-full w-full text-white p-0.5" />}
              </span>
              <div className="flex flex-wrap items-baseline gap-x-3">
                <div className="font-serif text-lg text-primary">{step.title}</div>
                <div className="text-xs text-muted-foreground">
                  {stepDate.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                </div>
                {done && <span className="text-[10px] text-emerald-700 uppercase tracking-widest">Selesai</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{step.desc}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function BookingCard({ b, onUpdate }) {
  const [pkg, setPkg] = React.useState(null);

  React.useEffect(() => {
    if (b.package_id) {
      api.get(`/packages/${b.package_id}`).then((r) => setPkg(r.data)).catch(() => {});
    }
  }, [b.package_id]);

  return (
    <div className="bg-card border border-border rounded-lg p-7 lg:p-8" data-testid={`booking-card-${b.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <StatusBadge status={b.status} />
            <PayBadge ps={b.payment_status} />
          </div>
          <h3 className="font-serif text-2xl text-primary">{b.package_name}</h3>
          {b.equipment_id && b.equipment_name && (
            <p className="text-xs text-emerald-700 mt-1" data-testid={`my-equipment-${b.id}`}>
              + {b.equipment_name}
              {b.equipment_price_idr ? ` · Rp ${new Intl.NumberFormat("id-ID").format(b.equipment_price_idr)}` : ""}
              {b.pax && b.equipment_price_idr ? ` × ${b.pax} pax` : ""}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Booking ID: <span className="font-mono">{b.id.slice(-10)}</span> · {new Date(b.created_at).toLocaleDateString("id-ID")}
          </p>
        </div>
        {b.payment_status === "unpaid" && (
          <div className="text-right text-xs text-muted-foreground max-w-[220px]" data-testid={`pay-info-${b.id}`}>
            <p className="font-semibold text-primary">Belum Dibayar</p>
            <p className="mt-1">Transfer ke rekening di bawah &amp; upload bukti untuk verifikasi admin.</p>
          </div>
        )}
        {b.payment_status === "dp_paid" && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground">DP dibayar</p>
            <p className="font-serif text-lg text-accent">Rp {b.dp_amount_idr.toLocaleString("id-ID")}</p>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 pt-5 border-t border-border">
        <div className="flex items-center gap-3 text-sm">
          <Users className="h-4 w-4 text-accent" />
          <span>{b.pax} jamaah</span>
        </div>
        {pkg?.departure_date && (
          <div className="flex items-center gap-3 text-sm">
            <CalendarDays className="h-4 w-4 text-accent" />
            <span>Berangkat: {new Date(pkg.departure_date).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
        )}
        {b.departure_pref && (
          <div className="flex items-center gap-3 text-sm sm:col-span-2">
            <PackageIcon className="h-4 w-4 text-accent" />
            <span>Preferensi: {b.departure_pref}</span>
          </div>
        )}
      </div>

      {b.notes || (b.status || "new") === "new" || (b.status || "new") === "contacted" ? (
        <NotesEditor booking={b} onUpdate={onUpdate} />
      ) : null}

      <DocumentsSection booking={b} onUpdate={onUpdate} />

      <PaymentProofSection booking={b} onUpdate={onUpdate} />

      {b.status === "confirmed" && pkg?.departure_date && (
        <ManasikTimeline departureDate={pkg.departure_date} />
      )}
    </div>
  );
}

export default function MyBookings() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = React.useState([]);
  const [fetching, setFetching] = React.useState(true);

  const reload = React.useCallback(() => {
    setFetching(true);
    api.get("/my-bookings")
      .then((r) => setBookings(r.data))
      .catch(() => setBookings([]))
      .finally(() => setFetching(false));
  }, []);

  React.useEffect(() => {
    if (!loading && !user) {
      navigate("/login?next=/my-bookings");
      return;
    }
    if (user) reload();
  }, [loading, user, navigate, reload]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="my-bookings-page">
      <Helmet>
        <title>Booking Saya — BATIK Tour & Travel</title>
      </Helmet>
      <header className="bg-primary text-primary-foreground">
        <div className="container-x py-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="overline !text-accent">Portal Jamaah</p>
            <h1 className="font-serif text-3xl">Booking Saya</h1>
            <p className="text-white/70 mt-1">Assalamu&apos;alaikum, {user.name}</p>
          </div>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" className="h-10 w-10 rounded-full object-cover" />}
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/30 hover:bg-white/10"
              data-testid="pilgrim-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-12">
        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="mb-8 bg-muted/60 p-1 rounded-full inline-flex h-auto" data-testid="dashboard-tabs">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground inline-flex items-center gap-1.5">
              <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground inline-flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5" /> Booking
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground inline-flex items-center gap-1.5">
              <UserIcon className="h-3.5 w-3.5" /> Profil
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" data-testid="dashboard-tab-content">
            <DashboardSummary user={user} bookings={bookings} onResendVerification={() => {}} />
            <div className="mt-6">
              <ReferralCard />
            </div>
            {fetching ? null : bookings.length === 0 && (
              <div className="bg-card border border-border rounded-lg p-14 text-center max-w-xl mx-auto mt-6">
                <PackageIcon className="h-12 w-12 text-accent mx-auto mb-5" strokeWidth={1.2} />
                <h2 className="font-serif text-3xl text-primary mb-3">Belum ada booking</h2>
                <p className="text-muted-foreground mb-8">Anda belum mendaftar paket manapun. Jelajahi paket kami untuk memulai perjalanan suci Anda.</p>
                <button onClick={() => navigate("/packages")} className="btn-primary" data-testid="browse-packages-cta">Lihat Paket</button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings" data-testid="bookings-tab-content">
            {fetching ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-accent" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-14 text-center max-w-xl mx-auto">
                <PackageIcon className="h-12 w-12 text-accent mx-auto mb-5" strokeWidth={1.2} />
                <h2 className="font-serif text-3xl text-primary mb-3">Belum ada booking</h2>
                <p className="text-muted-foreground mb-8">Anda belum mendaftar paket manapun. Jelajahi paket Umrah & Haji kami untuk memulai perjalanan suci Anda.</p>
                <button onClick={() => navigate("/packages")} className="btn-primary">Lihat Paket</button>
              </div>
            ) : (
              <>
                <p className="text-muted-foreground mb-6">{bookings.length} booking terdaftar dengan email <span className="text-primary font-medium">{user.email}</span></p>
                <div className="space-y-5">
                  {bookings.map((b) => (<BookingCard key={b.id} b={b} onUpdate={reload} />))}
                </div>
                <div className="mt-8 p-5 bg-card border border-dashed border-accent/40 rounded-lg text-sm text-muted-foreground flex items-start gap-3">
                  <MessageCircle className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                  <div>
                    Butuh bantuan? Hubungi kami di <a href="https://wa.me/6282114294101" target="_blank" rel="noopener noreferrer" className="text-accent link-underline">WhatsApp +62 821-1429-4101</a> untuk konfirmasi jadwal atau instruksi transfer DP.
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="profile" data-testid="profile-tab-content">
            <ProfilePanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
